import { Button, Stack, SxProps, TextField, useTheme } from '@mui/material';
import { FC, RefObject } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { WalletProfileAuthHint } from './wallet-profile-auth-hint';
import { useAuth, useDialog } from '@/logic/state';

import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useInitVerifyUserEmail } from '../../services/_api';
import ImageBoxFuture from '@/components/shared/image-box-future';

const EmptyTransactionsIcon = '/icons/profile/Empty-transactions.svg';

interface WalletNotVerifiedInfoProps {
  myWalletRef: RefObject<HTMLDivElement>;
  accordionSx: SxProps;
}

export const WalletNotVerifiedInfo: FC<WalletNotVerifiedInfoProps> = ({
  myWalletRef,
  accordionSx,
}) => {
  const intl = useIntl();
  const theme = useTheme();

  const user = useAuth(s => s.user, shallow);
  const setDialog = useDialog(s => s.setDialog, shallow);

  const initVerifyUserEmail = useInitVerifyUserEmail();

  const handleVerifyEmailClick = () => {
    initVerifyUserEmail.safeMutateAsync(
      {},
      {
        onSuccess: () => {
          setDialog({
            wrapper: true,
            isWalletOtpEmailForm: true,
          });
        },
      }
    );
  };

  const handleChangeEmailClick = () => {
    setDialog({
      wrapper: true,
      isChangeEmailConfirmDialog: true,
    });
  };

  return (
    <Stack
      ref={myWalletRef}
      sx={{
        ...accordionSx,
        alignItems: 'center',
        paddingX: {
          mobile: '16px',
          tablet: '16px',
        },
        paddingY: {
          mobile: '16px',
          tablet: pxToRem(34),
        },
      }}
    >
      <Stack
        sx={{
          maxWidth: '462px',
        }}
      >
        <ImageBoxFuture
          img={EmptyTransactionsIcon}
          title={intl.formatMessage({
            id: 'alt.my_wallet',
          })}
          objectFit='contain'
          sx={{
            width: {
              mobile: '168px',
              tablet: pxToRem(180),
            },
            height: {
              mobile: '132px',
              tablet: pxToRem(140),
            },
            marginBottom: {
              mobile: '26px',
              tablet: pxToRem(32),
            },
            alignSelf: 'center',
          }}
        />

        <Stack
          component='p'
          sx={{
            marginBottom: {
              mobile: '24px',
              tablet: pxToRem(29),
            },
          }}
        >
          <WalletProfileAuthHint
            message={intl.formatMessage(
              { id: 'wallet.user_not_verified.profile' },
              { br: <br /> }
            )}
          />
        </Stack>

        <TextField
          id='wallet-not-verified-email'
          label={<FormattedMessage id='global.email' />}
          value={user?.email}
          variant='outlined'
          disabled={true}
          sx={{
            marginBottom: {
              mobile: '24px',
              tablet: pxToRem(32),
            },
          }}
        />

        <Button
          variant='contained'
          loading={initVerifyUserEmail.isLoading}
          sx={{
            marginBottom: '22px',
            fontSize: '16px',
          }}
          onClick={handleVerifyEmailClick}
        >
          <FormattedMessage id='wallet.verify_your_email' />
        </Button>

        <Button
          sx={{
            fontSize: '16px',
            fontFamily: theme.palette.typography.font.bold,
            color: theme.palette.grey[70],
          }}
          onClick={handleChangeEmailClick}
        >
          <FormattedMessage id='wallet.use_another_email' />
        </Button>
      </Stack>
    </Stack>
  );
};
